import { create } from 'zustand';
import type { WorldSlug } from '@/lib/worlds';
import {
  getPlaybackUrl,
  recordListeningEvent,
  recordListeningEventBeacon,
} from '@/features/story/api';

export type PlaybackRate = 0.75 | 1 | 1.25 | 1.5;

export type SleepTimerType = '15min' | '30min' | '1h' | 'endOfStory';

export interface SleepTimerState {
  type: SleepTimerType;
  /** Wall-clock ms timestamp when the timer fires. */
  expiresAt: number;
  /** Tick-updated remaining time in ms — drives the countdown label. */
  remainingMs: number;
}

const TIMER_DURATIONS_MS: Record<Exclude<SleepTimerType, 'endOfStory'>, number> = {
  '15min': 15 * 60 * 1000,
  '30min': 30 * 60 * 1000,
  '1h': 60 * 60 * 1000,
};

const FADE_DURATION_MS = 3000;
const FADE_STEP_MS = 50;

export interface LoadArgs {
  storyId: string;
  title: string;
  childId: string | null;
  childName: string | null;
  worldSlug: WorldSlug;
  totalDurationS: number;
}

/**
 * Track discriminant. The store serves two kinds of audio through the SAME
 * `<audio>` singleton and the SAME play/pause/seek/skip/rate/sleep-timer
 * mechanics:
 *
 *   - `'story'`           — a single personalised story (the default; the only
 *                           kind that emits listening events / unload beacons /
 *                           MediaSession entries / any storyId-keyed network).
 *   - `'series-combined'` — the season Gesamt-Hörbuch (a single combined MP3,
 *                           seriesId-keyed, NOT storyId-keyed). Reuses the
 *                           playback path verbatim; every per-story side-effect
 *                           is gated OFF for it.
 */
export type TrackKind = 'story' | 'series-combined' | 'catalog';

/**
 * Args for `loadCombined()` — the season Gesamt-Hörbuch entry point. The
 * combined MP3 URL is already signed on the series detail, so (unlike
 * story `load()`, which fetches a storyId-keyed presigned URL) this seats
 * the src synchronously and keeps the user-gesture chain intact on iOS.
 */
export interface LoadCombinedArgs {
  url: string;
  downloadUrl: string | null;
  title: string;
  seriesId: string;
  durationMs: number;
}

/**
 * Args for `loadCatalog()` — the Wissenswelten catalog Folge entry point. Like
 * `loadCombined`, the catalog audio URL is already signed (fetched on the play
 * gesture from GET /api/catalog/.../playback), so this seats the src
 * synchronously and keeps the iOS gesture chain intact. Sets `kind='catalog'`,
 * `storyId=null` → every storyId-keyed side-effect (listening events, unload
 * beacon) is gated OFF, exactly like 'series-combined'. Duration comes from the
 * playback response (CatalogEpisodeAudio.audioDurationS); when null we fall back
 * to 0 and `effectiveDuration` reads the length off the element instead.
 */
export interface LoadCatalogArgs {
  url: string;
  downloadUrl: string | null;
  title: string;
  durationS: number | null;
}

export interface AudioStoreState {
  // Which kind of track is loaded. Drives the per-story side-effect gates
  // (listening events, unload beacon, MediaSession). Default 'story'.
  kind: TrackKind;
  // Series id for the 'series-combined' kind (null for stories). Lets the
  // season player tell whether the active combined track is ITS season.
  seriesId: string | null;

  // Loaded story metadata
  storyId: string | null;
  title: string | null;
  childId: string | null;
  childName: string | null;
  worldSlug: WorldSlug | null;
  totalDurationS: number;

  // Schlaf-Timer — null when inactive, set by setSleepTimer(), cleared
  // on expiry (after fade-out) or explicit clearSleepTimer().
  sleepTimer: SleepTimerState | null;

  // Playback state
  url: string | null;
  /**
   * Download URL — populated alongside `url` by the same backend call.
   * Points at the same asset but signed with `Content-Disposition:
   * attachment; filename="…"`. Consumers use it for `<a target="_blank">`
   * download links; the streaming `<audio>.src` keeps using `url` so the
   * browser plays inline without downloading.
   */
  downloadUrl: string | null;
  urlExpiresAt: number | null;
  isPlaying: boolean;
  currentTimeS: number;
  playbackRate: PlaybackRate;
  status: 'idle' | 'loading' | 'ready' | 'error';
  errorMessage: string | null;

  // Session + first-play flag
  sessionId: string | null;
  hasPlayedOnce: boolean;
  _startedEventSent: boolean;

  // Non-reactive DOM ref (consumers don't subscribe)
  _audio: HTMLAudioElement | null;

  // External actions
  bindAudio: (el: HTMLAudioElement | null) => void;
  load: (args: LoadArgs) => Promise<void>;
  loadCombined: (args: LoadCombinedArgs) => void;
  loadCatalog: (args: LoadCatalogArgs) => void;
  play: () => Promise<void>;
  pause: () => void;
  toggle: () => Promise<void>;
  seek: (seconds: number) => void;
  skipBy: (deltaSeconds: number) => void;
  setRate: (rate: PlaybackRate) => void;
  setSleepTimer: (type: SleepTimerType) => void;
  clearSleepTimer: () => void;
  close: () => void;
  fireUnloadBeacon: () => void;

  // Internal listeners (wired from AppShell's <audio>)
  _onTimeUpdate: (s: number) => void;
  _onPlay: () => void;
  _onPause: () => void;
  _onEnded: () => void;
  _onError: (msg: string) => void;
}

// ── Abandon threshold ──────────────────────────────────────────

/**
 * `abandoned` dispatches only when the user listened for at least
 * `ABANDON_MIN_SECONDS` AND stopped strictly before
 * `ABANDON_MAX_PCT * totalDurationS`. Exactly-5-seconds and
 * exactly-90%-reached are NOT abandons (strict lower / upper bounds).
 * Zero-duration stories never dispatch abandon.
 */
export const ABANDON_MIN_SECONDS = 5;
export const ABANDON_MAX_PCT = 0.9;

export function shouldDispatchAbandon(currentTimeS: number, totalDurationS: number): boolean {
  if (totalDurationS <= 0) return false;
  if (currentTimeS <= ABANDON_MIN_SECONDS) return false;
  if (currentTimeS >= totalDurationS * ABANDON_MAX_PCT) return false;
  return true;
}

// ── Effective duration ─────────────────────────────────────────

/**
 * The authoritative clamp ceiling for `seek()` / `skipBy()`. Prefers the
 * `<audio>` element's own `duration` property (populated from the streaming
 * MP3's metadata by the browser) over the store's `totalDurationS` — the
 * store value comes from `story.audioDurationS` in the backend response,
 * which is sometimes null, flows into `load()` as `?? 0`, and then makes
 * every clamp return 0. Element-reported duration is always correct once
 * the metadata has buffered.
 *
 * Falls back to the store value when element duration is NaN (pre-metadata
 * state, very early in playback). Last resort: `Infinity` — no upper clamp,
 * relying on the element's own boundary behaviour to stop at the end.
 */
function effectiveDuration(audio: HTMLAudioElement, storeDuration: number): number {
  if (Number.isFinite(audio.duration) && audio.duration > 0) return audio.duration;
  if (storeDuration > 0) return storeDuration;
  return Infinity;
}

// ── Store ──────────────────────────────────────────────────────

// Non-reactive timer IDs — intentionally scoped to the module (NOT part
// of the Zustand state shape) so RESET_STATE in test files doesn't have
// to carry these imperative concerns. Reset via
// `__resetSleepIntervalsForTests()` in test teardown.
let _sleepTickIntervalId: number | null = null;
let _sleepFadeIntervalId: number | null = null;

function clearSleepIntervals() {
  if (_sleepTickIntervalId !== null) {
    clearInterval(_sleepTickIntervalId);
    _sleepTickIntervalId = null;
  }
  if (_sleepFadeIntervalId !== null) {
    clearInterval(_sleepFadeIntervalId);
    _sleepFadeIntervalId = null;
  }
}

/** Test-only: force-clear the module-level setInterval IDs. */
export function __resetSleepIntervalsForTests() {
  clearSleepIntervals();
}

/**
 * Fade the audio volume from its current value to 0 over FADE_DURATION_MS,
 * then pause and clear the timer. Volume resets to 1 afterwards so the
 * next playback session starts at full volume. Called when the Schlaf-
 * Timer's expiresAt is reached (tick callback OR setSleepTimer with a
 * zero duration).
 */
function startSleepFade(
  get: () => AudioStoreState,
  set: (partial: Partial<AudioStoreState>) => void,
) {
  if (_sleepFadeIntervalId !== null) return; // already fading

  if (_sleepTickIntervalId !== null) {
    clearInterval(_sleepTickIntervalId);
    _sleepTickIntervalId = null;
  }

  const audio = get()._audio;
  if (!audio) {
    // No audio bound (tests or never-played state): just clear state.
    set({ sleepTimer: null });
    return;
  }

  const steps = FADE_DURATION_MS / FADE_STEP_MS;
  const startVolume = audio.volume;
  const decrement = startVolume / steps;
  let stepsRemaining = steps;

  // Flip the label to 0:00 so the UI doesn't overshoot into negative time.
  const current = get().sleepTimer;
  if (current) {
    set({ sleepTimer: { ...current, remainingMs: 0 } });
  }

  _sleepFadeIntervalId = window.setInterval(() => {
    const el = get()._audio;
    if (!el) {
      clearSleepIntervals();
      set({ sleepTimer: null });
      return;
    }
    stepsRemaining -= 1;
    if (stepsRemaining <= 0) {
      el.volume = 0;
      el.pause();
      el.volume = 1; // reset for next play
      clearSleepIntervals();
      set({ sleepTimer: null });
    } else {
      el.volume = Math.max(0, el.volume - decrement);
    }
  }, FADE_STEP_MS);
}

export const useAudioStore = create<AudioStoreState>((set, get) => ({
  kind: 'story',
  seriesId: null,
  storyId: null,
  title: null,
  childId: null,
  childName: null,
  worldSlug: null,
  totalDurationS: 0,

  sleepTimer: null,

  url: null,
  downloadUrl: null,
  urlExpiresAt: null,
  isPlaying: false,
  currentTimeS: 0,
  playbackRate: 1,
  status: 'idle',
  errorMessage: null,

  sessionId: null,
  hasPlayedOnce: false,
  _startedEventSent: false,

  _audio: null,

  bindAudio: (el) => set({ _audio: el }),

  load: async (args) => {
    const prev = get();

    // Dispatch abandoned for the previously-loaded story (if the user
    // left it in a partial-listen state). Gated to kind==='story' — a
    // previous 'series-combined' track never emits listening events.
    if (
      prev.kind === 'story' &&
      prev.storyId &&
      prev.sessionId &&
      shouldDispatchAbandon(prev.currentTimeS, prev.totalDurationS)
    ) {
      void recordListeningEvent({
        storyId: prev.storyId,
        ...(prev.childId ? { childId: prev.childId } : {}),
        deviceType: 'pwa',
        eventType: 'abandoned',
        playbackPositionS: Math.floor(prev.currentTimeS),
        totalDurationS: prev.totalDurationS,
        sessionId: prev.sessionId,
      });
    }

    // Stop previous playback synchronously before swapping src.
    if (prev._audio && !prev._audio.paused) {
      prev._audio.pause();
    }

    // New story = new session: any active Schlaf-Timer from a prior story
    // is no longer meaningful. Also reset volume in case a fade was in flight.
    clearSleepIntervals();
    if (prev._audio) prev._audio.volume = 1;

    const sessionId = crypto.randomUUID();
    set({
      kind: 'story',
      seriesId: null,
      storyId: args.storyId,
      title: args.title,
      childId: args.childId,
      childName: args.childName,
      worldSlug: args.worldSlug,
      totalDurationS: args.totalDurationS,
      sleepTimer: null,
      url: null,
      downloadUrl: null,
      urlExpiresAt: null,
      isPlaying: false,
      currentTimeS: 0,
      playbackRate: 1,
      status: 'loading',
      errorMessage: null,
      sessionId,
      hasPlayedOnce: false,
      _startedEventSent: false,
    });

    try {
      const { url, downloadUrl, expiresAt } = await getPlaybackUrl(args.storyId);
      set({
        url,
        downloadUrl: downloadUrl ?? null,
        urlExpiresAt: new Date(expiresAt).getTime(),
        status: 'ready',
      });
      const audio = get()._audio;
      if (audio) {
        audio.src = url;
        audio.playbackRate = 1;
      }
    } catch (err) {
      set({
        status: 'error',
        errorMessage: err instanceof Error ? err.message : 'load_failed',
      });
    }
  },

  /**
   * Load the season Gesamt-Hörbuch into the SAME singleton `<audio>` the
   * single-story player uses. Sets `kind='series-combined'` and carries
   * `seriesId` (NOT a storyId), so every per-story side-effect downstream
   * (play/pause/skip events, unload beacon, MediaSession) is gated off.
   *
   * The combined MP3 URL is already signed, so the src is seated
   * synchronously (`status='ready'` immediately) — no `await` stands
   * between the caller's user-gesture and the subsequent `play()`, which
   * is what keeps iOS Safari's gesture token valid. Mirrors `load()`'s
   * previous-track cleanup (pause + clear sleep intervals + reset volume +
   * abandon-dispatch for a previous STORY) so switching from a half-heard
   * story to the combined audiobook still records that story's abandon.
   */
  loadCombined: (args) => {
    const prev = get();

    if (
      prev.kind === 'story' &&
      prev.storyId &&
      prev.sessionId &&
      shouldDispatchAbandon(prev.currentTimeS, prev.totalDurationS)
    ) {
      void recordListeningEvent({
        storyId: prev.storyId,
        ...(prev.childId ? { childId: prev.childId } : {}),
        deviceType: 'pwa',
        eventType: 'abandoned',
        playbackPositionS: Math.floor(prev.currentTimeS),
        totalDurationS: prev.totalDurationS,
        sessionId: prev.sessionId,
      });
    }

    if (prev._audio && !prev._audio.paused) {
      prev._audio.pause();
    }

    clearSleepIntervals();
    if (prev._audio) prev._audio.volume = 1;

    set({
      kind: 'series-combined',
      seriesId: args.seriesId,
      storyId: null,
      title: args.title,
      childId: null,
      childName: null,
      worldSlug: null,
      totalDurationS: args.durationMs / 1000,
      sleepTimer: null,
      url: args.url,
      downloadUrl: args.downloadUrl,
      urlExpiresAt: null,
      isPlaying: false,
      currentTimeS: 0,
      playbackRate: 1,
      status: 'ready',
      errorMessage: null,
      sessionId: null,
      hasPlayedOnce: false,
      _startedEventSent: false,
    });

    const audio = get()._audio;
    if (audio) {
      audio.src = args.url;
      audio.playbackRate = 1;
    }
  },

  loadCatalog: (args) => {
    const prev = get();

    // Flush an abandon event for a PREVIOUS story track before switching, same
    // as loadCombined. Catalog itself never emits (kind!=='story').
    if (
      prev.kind === 'story' &&
      prev.storyId &&
      prev.sessionId &&
      shouldDispatchAbandon(prev.currentTimeS, prev.totalDurationS)
    ) {
      void recordListeningEvent({
        storyId: prev.storyId,
        ...(prev.childId ? { childId: prev.childId } : {}),
        deviceType: 'pwa',
        eventType: 'abandoned',
        playbackPositionS: Math.floor(prev.currentTimeS),
        totalDurationS: prev.totalDurationS,
        sessionId: prev.sessionId,
      });
    }

    if (prev._audio && !prev._audio.paused) {
      prev._audio.pause();
    }

    clearSleepIntervals();
    if (prev._audio) prev._audio.volume = 1;

    set({
      kind: 'catalog',
      seriesId: null,
      storyId: null,
      title: args.title,
      childId: null,
      childName: null,
      worldSlug: null,
      totalDurationS: args.durationS ?? 0,
      sleepTimer: null,
      url: args.url,
      downloadUrl: args.downloadUrl,
      urlExpiresAt: null,
      isPlaying: false,
      currentTimeS: 0,
      playbackRate: 1,
      status: 'ready',
      errorMessage: null,
      sessionId: null,
      hasPlayedOnce: false,
      _startedEventSent: false,
    });

    const audio = get()._audio;
    if (audio) {
      audio.src = args.url;
      audio.playbackRate = 1;
    }
  },

  play: async () => {
    const { _audio, url, status } = get();
    if (!_audio || !url || status !== 'ready') return;

    try {
      // Replay-after-end recovery. When the HTMLMediaElement has played
      // to completion, `audio.ended` is true and `audio.currentTime`
      // sits at `duration`. Sebastian's smoke-test confirmed that on
      // iOS Safari + desktop Chrome, a plain `currentTime = 0` reset
      // is NOT sufficient — the element stays in a semi-broken state
      // where subsequent seek/skip assignments get silently overwritten
      // by an internal reset-to-0. Full `_audio.load()` is the only
      // reliable recovery: it aborts any in-flight resource selection,
      // re-resolves metadata (duration), clears `ended`, and seats the
      // element back at `currentTime = 0` cleanly. The `src` attribute
      // remains set, so `load()` re-selects the same (presigned) URL —
      // with cache warm this is effectively instant and doesn't
      // perceptibly stutter playback.
      //
      // Runs synchronously before `await _audio.play()` so the
      // user-gesture chain holds on iOS (gesture token covers both
      // `load()` and the subsequent `play()` within the same handler).
      if (_audio.ended) {
        _audio.load();
      }
      // Important: this must run synchronously inside a user-gesture
      // handler on iOS Safari. Callers MUST have pre-fetched the URL via
      // `load()` on story-page mount so that by the time the user taps
      // "Audio anhören", `status === 'ready'` and no `await` stands
      // between the click and `.play()`.
      await _audio.play();

      const { kind, storyId, sessionId, currentTimeS, totalDurationS, childId, _startedEventSent } =
        get();
      if (kind === 'story' && storyId && sessionId) {
        void recordListeningEvent({
          storyId,
          ...(childId ? { childId } : {}),
          deviceType: 'pwa',
          eventType: _startedEventSent ? 'resumed' : 'started',
          playbackPositionS: Math.floor(currentTimeS),
          totalDurationS,
          sessionId,
        });
        set({ _startedEventSent: true, hasPlayedOnce: true });
      }
    } catch (err) {
      set({
        errorMessage: err instanceof Error ? err.message : 'play_failed',
      });
    }
  },

  pause: () => {
    const { _audio, kind, storyId, sessionId, childId, currentTimeS, totalDurationS } = get();
    if (!_audio) return;
    _audio.pause();
    if (kind === 'story' && storyId && sessionId) {
      void recordListeningEvent({
        storyId,
        ...(childId ? { childId } : {}),
        deviceType: 'pwa',
        eventType: 'paused',
        playbackPositionS: Math.floor(currentTimeS),
        totalDurationS,
        sessionId,
      });
    }
  },

  toggle: async () => {
    const { isPlaying, play, pause } = get();
    if (isPlaying) {
      pause();
    } else {
      await play();
    }
  },

  seek: (seconds) => {
    const { _audio, totalDurationS, sleepTimer } = get();
    if (!_audio) return;
    const duration = effectiveDuration(_audio, totalDurationS);
    const clamped = Math.max(0, Math.min(seconds, duration));
    _audio.currentTime = clamped;

    const update: Partial<AudioStoreState> = { currentTimeS: clamped };

    // Re-anchor endOfStory timer so a scrub forward/backward keeps the
    // "stop at the end of this story" semantics correct.
    if (sleepTimer?.type === 'endOfStory') {
      const now = Date.now();
      const remainingMs = Math.max(0, Math.floor((duration - clamped) * 1000));
      update.sleepTimer = {
        type: 'endOfStory',
        expiresAt: now + remainingMs,
        remainingMs,
      };
    }

    set(update);
  },

  skipBy: (delta) => {
    const { _audio, kind, totalDurationS, storyId, sessionId, childId, sleepTimer } = get();
    if (!_audio) return;
    // Read position from the DOM element (source of truth) — the store's
    // `currentTimeS` can lag if `timeupdate` sync is delayed or stalls
    // (e.g. around replay-after-end transitions). Clamp against the
    // element-authoritative duration via effectiveDuration.
    const current = _audio.currentTime;
    const duration = effectiveDuration(_audio, totalDurationS);
    const next = Math.max(0, Math.min(current + delta, duration));
    _audio.currentTime = next;

    const update: Partial<AudioStoreState> = { currentTimeS: next };

    if (sleepTimer?.type === 'endOfStory') {
      const now = Date.now();
      const remainingMs = Math.max(0, Math.floor((duration - next) * 1000));
      update.sleepTimer = {
        type: 'endOfStory',
        expiresAt: now + remainingMs,
        remainingMs,
      };
    }

    set(update);

    if (kind === 'story' && storyId && sessionId) {
      void recordListeningEvent({
        storyId,
        ...(childId ? { childId } : {}),
        deviceType: 'pwa',
        eventType: delta >= 0 ? 'skipped_forward' : 'skipped_backward',
        playbackPositionS: Math.floor(next),
        totalDurationS,
        sessionId,
      });
    }
  },

  setRate: (rate) => {
    const { _audio } = get();
    if (_audio) _audio.playbackRate = rate;
    set({ playbackRate: rate });
  },

  setSleepTimer: (type) => {
    // Replace any existing timer + reset any in-flight fade.
    clearSleepIntervals();
    const { totalDurationS, currentTimeS, _audio } = get();
    if (_audio) _audio.volume = 1;

    const now = Date.now();
    const durationMs =
      type === 'endOfStory'
        ? Math.max(0, Math.floor((totalDurationS - currentTimeS) * 1000))
        : TIMER_DURATIONS_MS[type];

    const expiresAt = now + durationMs;
    set({ sleepTimer: { type, expiresAt, remainingMs: durationMs } });

    // Edge: expiring immediately (e.g. endOfStory called when already at end).
    if (durationMs <= 0) {
      startSleepFade(get, set);
      return;
    }

    // Tick loop updates remainingMs each second; fires fade when ≤ 0.
    _sleepTickIntervalId = window.setInterval(() => {
      const { sleepTimer: current } = get();
      if (!current) {
        clearSleepIntervals();
        return;
      }
      const remainingMs = current.expiresAt - Date.now();
      if (remainingMs <= 0) {
        startSleepFade(get, set);
        return;
      }
      set({ sleepTimer: { ...current, remainingMs } });
    }, 1000);
  },

  clearSleepTimer: () => {
    clearSleepIntervals();
    const { _audio } = get();
    if (_audio) _audio.volume = 1;
    set({ sleepTimer: null });
  },

  close: () => {
    const { _audio, kind, storyId, sessionId, childId, currentTimeS, totalDurationS } = get();

    clearSleepIntervals();

    if (_audio) {
      _audio.pause();
      _audio.volume = 1;
      _audio.removeAttribute('src');
      // audio.load() here resets internal state so the next load() starts clean.
      _audio.load();
    }

    if (
      kind === 'story' &&
      storyId &&
      sessionId &&
      shouldDispatchAbandon(currentTimeS, totalDurationS)
    ) {
      void recordListeningEvent({
        storyId,
        ...(childId ? { childId } : {}),
        deviceType: 'pwa',
        eventType: 'abandoned',
        playbackPositionS: Math.floor(currentTimeS),
        totalDurationS,
        sessionId,
      });
    }

    set({
      kind: 'story',
      seriesId: null,
      storyId: null,
      title: null,
      childId: null,
      childName: null,
      worldSlug: null,
      totalDurationS: 0,
      sleepTimer: null,
      url: null,
      downloadUrl: null,
      urlExpiresAt: null,
      isPlaying: false,
      currentTimeS: 0,
      playbackRate: 1,
      status: 'idle',
      errorMessage: null,
      sessionId: null,
      hasPlayedOnce: false,
      _startedEventSent: false,
    });
  },

  /**
   * Called from AppShell's `pagehide` listener. Must be synchronous and
   * use `keepalive: true` so the request survives page tear-down. Only
   * fires if the user had actually played back and the abandon threshold
   * is met — a user who loaded but never played is not an "abandon".
   */
  fireUnloadBeacon: () => {
    const { kind, storyId, sessionId, childId, currentTimeS, totalDurationS, hasPlayedOnce } =
      get();
    if (kind !== 'story' || !storyId || !sessionId || !hasPlayedOnce) return;
    if (!shouldDispatchAbandon(currentTimeS, totalDurationS)) return;
    recordListeningEventBeacon({
      storyId,
      ...(childId ? { childId } : {}),
      deviceType: 'pwa',
      eventType: 'abandoned',
      playbackPositionS: Math.floor(currentTimeS),
      totalDurationS,
      sessionId,
    });
  },

  _onTimeUpdate: (s) => set({ currentTimeS: s }),
  _onPlay: () => set({ isPlaying: true }),
  _onPause: () => set({ isPlaying: false }),
  _onEnded: () => {
    const { kind, storyId, sessionId, childId, totalDurationS } = get();
    if (kind === 'story' && storyId && sessionId) {
      void recordListeningEvent({
        storyId,
        ...(childId ? { childId } : {}),
        deviceType: 'pwa',
        eventType: 'completed',
        playbackPositionS: totalDurationS,
        totalDurationS,
        sessionId,
      });
    }
    set({ isPlaying: false, currentTimeS: 0, _startedEventSent: false });
  },
  _onError: (msg) => set({ status: 'error', errorMessage: msg, isPlaying: false }),
}));
